import { servicePost } from "./client.ts";
import { batchedServicePost } from "./readBatch.ts";

/* ── Kiosk types ─────────────────────────────────────────── */

export interface KioskKiosk {
  KioskId: number;
  LocationID: number;
  StoreName: string;
  StoreNumber: string | null;
  Name: string | null;
  City: string;
  Adress: string;
  ZipCode: string;
  Country: string;
  District: string;
  County: string | null;
  Chain: string | null;
  Type: string;
  KioskType: number;
  Capacity: number;
  Status: number;
  InHouseStatus: string;
  SerialNumber: string | null;
  IPNumber: string | null;
  ExternalReference: string | null;
  Placement: string | null;
  PlacementNotes: string | null;
  Position: string | null;
  Amplifier: number;
  Washers: boolean;
  DropSlot: boolean;
  InnerDoorWindows: number;
  Comments: string | null;
  TestString: string | null;
  sInstallDate: string | null;
  dInstallDate: string | null;
  RemovalDate: string | null;
  UPSReplace: string | null;
  BeltReplace: string | null;
  EjectRepair: string | null;
  RevenueRanking: number;
  RevenueRankingData: string;
  SimPin: string;
  SimPuk: string;
  SimNumber: string;
  KioskDefined1: string | null;
  KioskDefined2: string | null;
  KioskDefined3: string | null;
  ExportStamp01: string;
  ExportStamp: string;
  TS: number;
}

/* ── API functions ───────────────────────────────────────── */

export function getKioskKiosk(kioskID: number) {
  return batchedServicePost<KioskKiosk>("WebGetKioskKiosk", { kioskID });
}

export function saveKiosk(kiosk: KioskKiosk, tocreate: number) {
  return servicePost<number>("WebSaveKiosk", { kiosk, tocreate });
}

export function getKioskInHouseStatuses() {
  return batchedServicePost<string[]>("WebGetKioskInHouseStatuses", {});
}

export function getKioskQrStaticGuid(kioskID: number) {
  return batchedServicePost<string>("WebGetKioskQrStaticGuid", { kioskID });
}

export function saveKioskIdInInstantInventory(kioskId: number, serialNumber: string) {
  return servicePost<boolean>("WebSaveKioskIdInInstantInventory", { kioskId, serialNumber });
}

export function sendRestartKiosk(kioskId: number) {
  return servicePost<boolean>("SendRestartKiosk", { kioskId });
}

export function sendStatusCommand(command: string, kiosk: number, args: Record<string, string> = {}) {
  return servicePost<boolean>("SendStatusCommand", { command, kiosk, args });
}

/**
 * Mint a single-sign-on link into the InstantRelay VNC repeater.
 * Success → oData is a fully-formed URL (`https://<relay>/sso#token=<jwt>&to=...`).
 * `target` "kiosk" lands on the kiosk's connect page; "console" lands on the relay
 * super console (/admin) and is superadmin-gated server-side. Gated to editkiosk-write.
 * The token rides in the URL fragment and is short-lived (~60s): open it immediately,
 * and never log or persist oData.
 */
export function mintRelayLink(kioskId: number, target: "kiosk" | "console" = "kiosk") {
  return servicePost<string>("MintRelayLink", { kioskId, target });
}

/* ── Temperature Settings types ──────────────────────────── */

export interface TempIQBox {
  PiID: number;
  Name: string;
  Position: number;
  TemperatureAlarmDiff: number;
  TemperatureValue: number;
  TempSensorOfflineEnabled: boolean;
}

export interface TempSetting {
  sKey: string;
  sValue: string;
  sDescription: string;
}

export interface TempPayload {
  IqLevelSettings: TempIQBox[];
  KioskLevelSettings: TempSetting[];
  Message: string;
}

export function getTemperatureSettingData(kioskID: number) {
  return batchedServicePost<TempPayload>("WebGetTemperatureSettingData", { kioskID });
}

export function setTemperatureSettingData(kioskID: number, data: TempPayload) {
  return servicePost<boolean>("WebSetTemperatureSettingData", { kioskID, data });
}

/* ── Door-open events (temperature graph annotations) ────── */

export interface KioskDoorOpening {
  /** IQ box id — equals TempIQBox.PiID. */
  IQBoxID: number;
  /** Kiosk id the door event belongs to. */
  KioskID: number;
  /** Seconds the door stayed open. */
  SecondsOpened: number;
  /** UTC instant the door opened (ISO-8601 with trailing Z). */
  OpenedUtc: string;
  /** UTC instant the door closed (ISO-8601 with trailing Z). */
  ClosedUtc: string;
}

/**
 * Door-open events for a kiosk within an absolute UTC window. The window is
 * derived from the visible temperature series so annotations align with it.
 */
export function getKioskDoorOpenings(kioskID: number, fromUtc: string, toUtc: string) {
  return servicePost<KioskDoorOpening[]>("WebGetKioskDoorOpenings", { kioskID, fromUtc, toUtc });
}

/**
 * Door-open events for a set of chart series ids (IQ box PiIDs and/or kiosk ids)
 * within an absolute UTC window. Used by the standalone temperature charts, where
 * no single kiosk id is fixed — the kiosk is chosen via a filter, so the visible
 * chart series (PiID for sensor charts, kiosk id for median charts) drive the lookup.
 */
export function getDoorOpeningsForChart(idList: number[], fromUtc: string, toUtc: string) {
  return servicePost<KioskDoorOpening[]>("WebGetDoorOpeningsForChart", { idList, fromUtc, toUtc });
}

/* ── Open Hours types ────────────────────────────────────── */

export interface OpenHourType {
  ID: number;
  From: string;
  To: string;
  Day: number;
  OverridePrio: number;
  IsClosed: boolean;
  ValidTo?: string;
  CreatedBy: number;
  Type: number;
  Deleted: boolean;
  /* Energy saver options, only used when Type = 3. */
  EnergySaverLedOff?: boolean;
  EnergySaverLowerTemp?: boolean;
  EnergySaverTempOffset?: number;
  EnergySaverBlackScreen?: boolean;
  TypeString?: string;
  ToTime: string;
  FromTime: string;
  TheDay?: string;
  TheShop?: string;
}

export function getActiveOpenHours(kioskid: number) {
  return batchedServicePost<OpenHourType[]>("WebGetActiveOpenHours", { kioskid });
}

export function getAvailableOpenHourTypes(kioskid: number) {
  return batchedServicePost<OpenHourType[]>("WebGetAvailableOpenHourTypes", { kioskid });
}

export function newOpenHourType(oht: OpenHourType) {
  return servicePost<boolean>("WebNewOpenHourType", { oht });
}

export function saveKioskOpenHour(oht: OpenHourType[], kioskid: number) {
  return servicePost<boolean>("WebSaveKioskOpenHour", { oht, kioskid });
}

export function saveOpenHourType(oht: OpenHourType) {
  return servicePost<boolean>("WebSaveOpenHourType", { oht });
}
